import { useState, useRef, useEffect } from 'react';
import AustraliaImage from "@/assets/images/common/Australia.webp";
import NewZealandImage from "@/assets/images/common/NewZealand.webp";
import JapanImage from "@/assets/images/common/Japan.webp";
import USAImage from "@/assets/images/common/USA.webp";
import NepalImage from "@/assets/images/common/Nepal.webp";

const locations = [
	{ id: 1, name: 'Australia', image: AustraliaImage, email: 'enquiries@sammicron.com', contact: '+61 2 1234 5678' },
	{ id: 2, name: 'New Zealand', image: NewZealandImage, email: 'enquiries@sammicron.com', contact: '+64 9 1234 5678' },
	{ id: 3, name: 'Japan', image: JapanImage, email: 'enquiries@sammicron.com', contact: '+81 3 1234 5678' },
	{ id: 4, name: 'USA', image: USAImage, email: 'enquiries@sammicron.com', contact: '+1 555 1234 5678' },
	{ id: 5, name: 'Nepal', image: NepalImage, email: 'enquiries@sammicron.com', contact: '+977 1 1234 5678' },
];

const LocationCard = ({ location, isActive, onToggle }) => {
	const cardRef = useRef<HTMLDivElement>(null);
	const nameRef = useRef<HTMLDivElement>(null);
	const [translateY, setTranslateY] = useState(0);

	useEffect(() => {
		if (cardRef.current && nameRef.current) {
			const cardHeight = cardRef.current.offsetHeight;
			const nameHeight = nameRef.current.offsetHeight;
			// Move name from bottom to top: subtract card height, add name height + top padding
			setTranslateY(-(cardHeight - nameHeight - 24));
		}
	}, []);

	return (
		<div
			ref={cardRef}
			className="relative h-72 sm:h-64 md:h-72 lg:h-80 cursor-pointer overflow-hidden rounded-2xl"
			onMouseEnter={onToggle}
			onMouseLeave={onToggle}
			onClick={onToggle}
		>
			{/* Background Image */}
			<img
				src={location.image}
				alt={location.name}
				className="w-full h-full object-cover"
				style={{
					transform: isActive ? 'scale(1.07)' : 'scale(1)',
					transition: 'transform 700ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
					willChange: 'transform',
				}}
			/>

			{/* Gradient Overlay */}
			<div
				className="absolute inset-0"
				style={{
					background: isActive
						? 'linear-gradient(to bottom, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.75) 100%)'
						: 'linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.15) 60%, transparent 100%)',
					transition: 'background 700ms ease',
				}}
			/>

			{/* Location Name — travels from bottom to top */}
			<div
				ref={nameRef}
				className="absolute bottom-0 left-0 right-0 px-4 sm:px-6 py-3"
				style={{
					transform: isActive ? `translateY(${translateY}px)` : 'translateY(0px)',
					transition: 'transform 600ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
					willChange: 'transform',
				}}
			>
				<h3 className="text-white text-xl font-semibold drop-shadow-lg">{location.name}</h3>
			</div>

			{/* Contact Info — slides up from below */}
			<div
				className="absolute bottom-0 left-0 right-0 p-4 sm:p-6"
				style={{
					transform: isActive ? 'translateY(0)' : 'translateY(100%)',
					opacity: isActive ? 1 : 0,
					transition: 'transform 600ms cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 400ms ease',
					willChange: 'transform',
				}}
			>
				<div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
					<p className="text-white/90 text-sm mb-1">Email us</p>
					<p className="text-white font-medium text-sm break-all">
						<a
							href={`mailto:${location.email}`}
							className="hover:underline"
							onClick={(e) => e.stopPropagation()}
						>
							{location.email}
						</a>
					</p>
				</div>
			</div>
		</div>
	);
};

export default function ContactPage() {
	const [activeCard, setActiveCard] = useState<number | null>(null);

	const handleToggle = (id: number) => {
		setActiveCard((prev) => (prev === id ? null : id));
	};

	return (
		<div>
			<div className="relative z-10 bg-[#1a1a1a]">
				<div className="container pt-16 pb-24 md:py-20 px-4 sm:px-6">
					<h4 className="text-white text-2xl md:text-3xl font-bold text-center max-w-3xl mx-auto mb-8 sm:mb-10 md:mb-12 leading-snug">
						Smart tech. Better business.
					</h4>

					<div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-12 md:gap-5">
						{locations.map((location) => (
							<div key={location.id} className="w-full">
								<LocationCard
									location={location}
									isActive={activeCard === location.id}
									onToggle={() => handleToggle(location.id)}
								/>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}